import { CustomCellEditorProps } from "ag-grid-react";
import { Select } from "antd";
import DesignReviewerDropdown from "components/design-reviwer-dropdown";
import GCReviewersDropdown from "components/gc-reveiewer-dropdown";
import ResponsibleDesignFirmsDropdown from "components/responsible-design-firms";
import DependedCompanyUserDropdownComponent from "components/responsible-designers";
import { ProjectContext } from "context/ProjectProvider";
import { getValueFromDotPath, setNestedValue } from "entity-app/utils/utils";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import {
  ProjectParticipants,
  useProjectParticipants
} from "hooks/project-participants";
import { useMemo, useContext } from "react";
import { GET_PROJECT_WORKFLOW_TEMPLATE_NAMES } from "services/graphQL/ciq-queries";

function WorkflowTemplateDropdown(props: any) {
  const { onChange, value, featureTypeId } = props;

  const { gqlClientForProject } = useContext(ProjectContext);
  const { data: projectWorkflowTemplates, loading } = useCIQQuery<{
    project_workflow_templates: Array<any>;
  }>(GET_PROJECT_WORKFLOW_TEMPLATE_NAMES, {
    client: gqlClientForProject,
    variables: { featureId: featureTypeId }
  });

  const options = useMemo(() => {
    const tooltip = "tooltip";
    if (
      projectWorkflowTemplates &&
      projectWorkflowTemplates.project_workflow_templates
    ) {
      const templates =
        projectWorkflowTemplates.project_workflow_templates.filter(
          (template: any) => !template.disabled || template.id === value
        );

      return templates.map((t) => ({
        label: t.name,
        value: t.id,
        tooltip
      }));
    }
    return [] as any[];
  }, [projectWorkflowTemplates, value]);

  return (
    <Select
      className="w-full"
      options={options}
      value={value}
      onChange={onChange}
      loading={loading}
      placeholder="Select a template"
      data-testid="dropdowneditor-select"
    />
  );
}

export function ResponsibleContractorDropdown(props: any) {
  const {
    onChange,
    value,
    participantsSource = "materialTradePartners"
  } = props;
  const { projectParticipants } = useProjectParticipants();
  const subContractors =
    projectParticipants[participantsSource as keyof ProjectParticipants];

  return (
    <Select
      className="w-full"
      autoFocus
      allowClear
      value={value || undefined}
      loading={!projectParticipants?.dataLoaded}
      options={
        subContractors
          ? subContractors.map((company: any) => ({
              label: company.subscription_vendor.name,
              value: company.vendor_id
            }))
          : []
      }
      onChange={onChange}
    />
  );
}

type Props = CustomCellEditorProps & {
  user: any;
};

function EntityDropdownCellEditor(props: Props) {
  const { stopEditing, value, user, data, onValueChange } = props;

  const options = useMemo(
    () => ({
      autoFocus: true,
      value,
      participantsSource: user.participantsSource,
      onChange: (val: string, option: any) => {
        const res = {} as any;
        setNestedValue(res, user.id, option?.value || null);
        if (option?.firstName) {
          setNestedValue(res, user.firstName, option.firstName);
          setNestedValue(res, user.lastName, option.lastName);
        } else {
          setNestedValue(res, user.firstName, option?.label || "");
          setNestedValue(res, user.lastName, undefined);
        }
        if (user.statusId) {
          setNestedValue(res, user.statusId, undefined);
        }
        if (user.companyObjField && option?.company) {
          setNestedValue(res, user.companyObjField, option.company);
        }
        onValueChange(res);
        setTimeout(() => {
          stopEditing();
        }, 200);
      }
    }),
    [onValueChange, stopEditing, user, value]
  );

  const dropDrop = useMemo(() => {
    switch (user.dropdownType) {
      case "RC_dropdown":
        return <ResponsibleContractorDropdown {...options} />;
      case "RDF_dropdown":
        return <ResponsibleDesignFirmsDropdown {...options} />;
      case "RD_dropdown": {
        const selectedCompanyFieldId = getValueFromDotPath(
          data,
          user.dependentId
        );
        return (
          <DependedCompanyUserDropdownComponent
            {...options}
            projectParticipantType="designReviewers"
            selectedCompanyFieldId={selectedCompanyFieldId}
            companyNotFoundContent={user.dependentNotFoundContent}
          />
        );
      }
      case "RC_ASSIGNEE_dropdown": {
        const selectedCompanyFieldId = getValueFromDotPath(
          data,
          user.dependentId
        );
        return (
          <DependedCompanyUserDropdownComponent
            {...options}
            projectParticipantType="submitterUsers"
            selectedCompanyFieldId={selectedCompanyFieldId}
          />
        );
      }
      case "Template_dropdown":
        return (
          <WorkflowTemplateDropdown
            {...options}
            featureTypeId={user.featureTypeId}
          />
        );
      case "DR_dropdown":
        return <DesignReviewerDropdown {...options} />;
      default:
        return <GCReviewersDropdown {...options} />;
    }
  }, [data, options, user]);

  return <div className="h-full flex items-end">{dropDrop}</div>;
}

export default EntityDropdownCellEditor;
